import type { AuthTokens } from '@/types';
import { hasCsrfCookie } from './authRefresh';

/**
 * In-memory access token only. The refresh token lives in an HttpOnly cookie
 * (`refresh_token`, Path=/api/v1/auth) and is not readable from JavaScript.
 * First-party cookies are required (Vite proxy in dev, same origin in prod).
 *
 * Leftover localStorage keys from the previous session store are wiped once
 * so a stolen XSS payload cannot read an old refresh token.
 */

let accessToken: string | null = null;
let expiresAt: number | null = null;

function wipeLegacyStorage(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem('wenkey.accessToken');
    window.localStorage.removeItem('wenkey.refreshToken');
    window.localStorage.removeItem('wenkey.expiresAt');
  } catch {
    /* private mode */
  }
}

wipeLegacyStorage();

export const tokenStorage = {
  getAccessToken(): string | null {
    return accessToken;
  },

  /** True when a refresh cookie session is likely present (readable csrf cookie). */
  hasRefreshSession(cookieSource?: string): boolean {
    return hasCsrfCookie(cookieSource);
  },

  isAccessTokenExpiring(skewMs = 30_000): boolean {
    if (!accessToken) return true;
    if (expiresAt === null) return false;
    return Date.now() + skewMs >= expiresAt;
  },

  set(tokens: Pick<AuthTokens, 'accessToken' | 'expiresIn'>): void {
    accessToken = tokens.accessToken;
    expiresAt = Date.now() + tokens.expiresIn * 1000;
  },

  clear(): void {
    accessToken = null;
    expiresAt = null;
    wipeLegacyStorage();
  },
};

type SessionExpiredListener = () => void;
const listeners = new Set<SessionExpiredListener>();

export function onSessionExpired(listener: SessionExpiredListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function emitSessionExpired(_failedRefreshToken?: string | null): void {
  tokenStorage.clear();
  listeners.forEach((listener) => listener());
}
